import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  ArrowBack,
  FilterList,
  CheckCircle,
  Error as ErrorIcon,
  Inbox,
  InfoOutlined,
  ExpandMore,
  FolderOpen,
  Description,
} from '@mui/icons-material';
import TitleBar from '../../components/TitleBar/TitleBar';
import { getToken } from '../../services/auth';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { CONFIG } from '../../config/config';
import { getReviewEntities, ReviewEntity, getInputMetricMetadata, type MetricInputMetadataResponse } from '../../services/api';
import { formatDisplayDate, formatDisplayDateTime, withGridDateColumns } from '../../utils/dateUtils';
import { formatMetricLevelLabel } from '../../utils/metricLevelUtils';

/* ======================================================
   Tree helper: flatten entities into a depth-ordered list
====================================================== */
interface TreeOption {
  entity: ReviewEntity;
  depth: number;
  hasChildren: boolean;
  prefix: string; // visual connector string
}

function buildEntityTree(entities: ReviewEntity[]): TreeOption[] {
  const idMap = new Map<number, ReviewEntity>();
  entities.forEach((e) => idMap.set(e.id, e));

  // Build children map
  const childrenMap = new Map<number, ReviewEntity[]>();
  const rootEntities: ReviewEntity[] = [];

  entities.forEach((e) => {
    const parentIds = e.parent_ids?.filter((pid) => idMap.has(pid)) || [];
    if (parentIds.length === 0) {
      rootEntities.push(e);
    } else {
      const pid = parentIds[0];
      if (!childrenMap.has(pid)) childrenMap.set(pid, []);
      childrenMap.get(pid)!.push(e);
    }
  });

  // Sort alphabetically
  const sortByName = (a: ReviewEntity, b: ReviewEntity) => a.name.localeCompare(b.name);
  rootEntities.sort(sortByName);
  childrenMap.forEach((children) => children.sort(sortByName));

  // DFS flatten with depth
  const result: TreeOption[] = [];
  const flatten = (entity: ReviewEntity, depth: number) => {
    const children = childrenMap.get(entity.id) || [];
    result.push({ entity, depth, hasChildren: children.length > 0, prefix: '' });
    children.forEach((child) => flatten(child, depth + 1));
  };
  rootEntities.forEach((root) => flatten(root, 0));

  return result;
}

// Map dimension names (lowercase) to the column key used in getColumnsForMetricType
const DIMENSION_TO_COLUMN_KEY: Record<string, string> = {
  'schedule': 'schedule',
  'effort': 'effort',
  'cost': 'cost',
  'scope': 'scope',
  'quality': 'quality',
  'engineering': 'engineering',
  'service request management': 'serviceRequest',
  'change management': 'changeManagement',
  'service configuration': 'serviceConfiguration',
  'incident management': 'incidentManagement',
  'problem management': 'problemManagement',
  'release & deployment management': 'releaseAndDeploymentManagement',
  'access management': 'accessManagement',
  'event management': 'eventManagement',
  'staffing': 'staffing',
  'staff quality': 'staffQuality',
  'obligations': 'obligations',
  'financial': 'financial',
  'financials': 'financial',
};

/* ======================================================
   Types
====================================================== */
type DimLoadStatus = 'idle' | 'loading' | 'loaded' | 'empty' | 'error';

interface DimensionState {
  status: DimLoadStatus;
  data: any[];
}

const STATUS_ORDER: Record<DimLoadStatus, number> = {
  loaded: 0,
  loading: 1,
  idle: 2,
  error: 3,
  empty: 4,
};

/* ======================================================
   Tab status icon
====================================================== */
const DimStatusIcon = ({ status }: { status: DimLoadStatus }) => {
  switch (status) {
    case 'loading':
      return <CircularProgress size={14} sx={{ ml: 0.75 }} />;
    case 'loaded':
      return <CheckCircle sx={{ ml: 0.75, fontSize: 16, color: 'success.main' }} />;
    case 'empty':
      return <Inbox sx={{ ml: 0.75, fontSize: 16, color: 'text.disabled' }} />;
    case 'error':
      return <ErrorIcon sx={{ ml: 0.75, fontSize: 16, color: 'error.main' }} />;
    default:
      return null;
  }
};

/* ======================================================
   Component
====================================================== */
const ViewMetricsData = () => {
  const navigate = useNavigate();

  // --- Filter options (loaded once) ---
  const [reviewEntities, setReviewEntities] = useState<ReviewEntity[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState<string | null>(null);

  // --- Selected filters ---
  const [selectedEntityId, setSelectedEntityId] = useState<number | ''>('');
  const [activeDimName, setActiveDimName] = useState('');
  const [filterEntityType, setFilterEntityType] = useState('');

  // --- Per-dimension data ---
  const [dimensionData, setDimensionData] = useState<Record<string, DimensionState>>({});
  const abortRef = useRef<AbortController | null>(null);

  // --- Metadata dialog ---
  const [metadataDialogOpen, setMetadataDialogOpen] = useState(false);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<MetricInputMetadataResponse | null>(null);

  const handleOpenMetadata = async () => {
    setMetadataDialogOpen(true);
    setMetadataLoading(true);
    setMetadataError(null);
    try {
      const res = await getInputMetricMetadata();
      setMetadata(res.data);
    } catch {
      setMetadataError('Failed to load metric input metadata');
    } finally {
      setMetadataLoading(false);
    }
  };

  // --- Filtered metadata dialog (per selected entity) ---
  const [filteredMetaDialogOpen, setFilteredMetaDialogOpen] = useState(false);
  const [filteredMetaLoading, setFilteredMetaLoading] = useState(false);
  const [filteredMetaError, setFilteredMetaError] = useState<string | null>(null);
  const [filteredMetadata, setFilteredMetadata] = useState<MetricInputMetadataResponse | null>(null);

  const handleOpenFilteredMetadata = async () => {
    if (!selectedEntityId) return;
    setFilteredMetaDialogOpen(true);
    setFilteredMetaLoading(true);
    setFilteredMetaError(null);
    try {
      const res = await getInputMetricMetadata();
      const all = res.data;
      // Filter each dimension's entries to only the selected entity
      const filtered: MetricInputMetadataResponse = {};
      for (const [dim, entries] of Object.entries(all)) {
        const match = entries.filter((e) => e.review_entity_id === selectedEntityId);
        if (match.length > 0) filtered[dim] = match;
      }
      setFilteredMetadata(filtered);
    } catch {
      setFilteredMetaError('Failed to load metric input metadata');
    } finally {
      setFilteredMetaLoading(false);
    }
  };

  /* ================= Load review entities on mount ================= */
  useEffect(() => {
    let cancelled = false;
    const loadOptions = async () => {
      try {
        const res = await getReviewEntities();
        if (!cancelled) setReviewEntities(res.data || []);
      } catch {
        if (!cancelled) setOptionsError('Failed to load review entities');
      } finally {
        if (!cancelled) setOptionsLoading(false);
      }
    };
    loadOptions();
    return () => { cancelled = true; };
  }, []);

  /* ================= Selected entity object ================= */
  const selectedEntity = useMemo(
    () => reviewEntities.find((e) => e.id === selectedEntityId),
    [reviewEntities, selectedEntityId]
  );

  /* ================= Entity type filter options ================= */
  const entityTypeOptions = useMemo(() => {
    const types = new Set(
      reviewEntities.map((e) => e.review_entity_type?.display_label).filter(Boolean)
    );
    return Array.from(types).sort() as string[];
  }, [reviewEntities]);

  const filteredEntities = useMemo(() => {
    const list = filterEntityType
      ? reviewEntities.filter((e) => e.review_entity_type?.display_label === filterEntityType)
      : reviewEntities;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }, [reviewEntities, filterEntityType]);

  /* ================= Tree-structured options for Autocomplete ================= */
  const treeOptions = useMemo(() => buildEntityTree(filteredEntities), [filteredEntities]);

  // Reset entity when entity type changes
  useEffect(() => { setSelectedEntityId(''); }, [filterEntityType]);

  /* ================= Applicable dimensions for the selected entity type ================= */
  const applicableDimensionNames: string[] = useMemo(() => {
    const names = selectedEntity?.review_entity_type?.associated_review_dimension_names;
    if (!names || names.length === 0) return [];
    return names as string[];
  }, [selectedEntity]);

  /* ================= Sorted dimensions: loaded first, empty/error last ================= */
  const sortedDimensions = useMemo(() => {
    return [...applicableDimensionNames].sort((a, b) => {
      const sa = dimensionData[a]?.status || 'idle';
      const sb = dimensionData[b]?.status || 'idle';
      return STATUS_ORDER[sa] - STATUS_ORDER[sb];
    });
  }, [applicableDimensionNames, dimensionData]);

  /* ================= Map active dimension name to sorted tab index ================= */
  const activeTabIndex = useMemo(() => {
    const idx = sortedDimensions.findIndex((d) => d === activeDimName);
    return idx >= 0 ? idx : 0;
  }, [sortedDimensions, activeDimName]);

  const handleTabChange = (_: React.SyntheticEvent, newIndex: number) => {
    const dim = sortedDimensions[newIndex];
    if (dim) setActiveDimName(dim);
  };

  /* ================= Fetch all applicable dimensions in parallel ================= */
  useEffect(() => {
    setDimensionData({});
    setActiveDimName('');

    if (!selectedEntityId || applicableDimensionNames.length === 0) return;

    setActiveDimName(applicableDimensionNames[0]);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const token = getToken();

    const initial: Record<string, DimensionState> = {};
    applicableDimensionNames.forEach((dim) => {
      initial[dim] = { status: 'loading', data: [] };
    });
    setDimensionData(initial);

    applicableDimensionNames.forEach((dim) => {
      axios
        .get(`${CONFIG.SERVER_URL}/get_metrics_by_review_entity`, {
          params: { review_entity_id: selectedEntityId, review_dimension: dim },
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        .then((res) => {
          const rows: any[] = res.data || [];
          setDimensionData((prev) => ({
            ...prev,
            [dim]: { status: rows.length > 0 ? 'loaded' : 'empty', data: rows },
          }));
        })
        .catch((err) => {
          if (axios.isCancel(err)) return;
          setDimensionData((prev) => ({
            ...prev,
            [dim]: { status: 'error', data: [] },
          }));
        });
    });

    return () => { controller.abort(); };
  }, [selectedEntityId, applicableDimensionNames]);

  /* ================= Current tab data ================= */
  const currentDimState = dimensionData[activeDimName];
  const metricsData = currentDimState?.data || [];
  const currentColumnKey = useMemo(() => {
    if (!activeDimName) return '';
    return DIMENSION_TO_COLUMN_KEY[activeDimName.toLowerCase()] || '';
  }, [activeDimName]);

  // Define columns for the DataGrid
  const METRIC_LEVEL_COLUMNS: GridColDef[] = [
    {
      field: 'metric_identifier_type',
      headerName: 'Metric Level',
      width: 140,
      renderCell: (params) => formatMetricLevelLabel(params.row.metric_identifier_type),
    },
    {
      field: 'metric_identifier_name',
      headerName: 'Level Instance',
      width: 180,
      renderCell: (params) => (params.value as string) || '',
    },
  ];

  const scheduleColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'baseline_start_date', headerName: 'Baseline Start Date', width: 160 },
    { field: 'baseline_end_date', headerName: 'Baseline End Date', width: 160 },
    { field: 'start_date', headerName: 'Actual Start Date', width: 160 },
    { field: 'end_date', headerName: 'Actual End Date', width: 160 }
  ];
  
  const effortColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'baseline_effort', headerName: 'Baseline Effort', width: 150 },
    { field: 'work_effort', headerName: 'Work Effort', width: 150 },
    { field: 'review_effort', headerName: 'Review Effort', width: 150 },
    { field: 'rework_effort', headerName: 'Rework Effort', width: 150 }
  ];
  
  const scopeColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'base_scope_complexity_points', headerName: 'Baseline Points', width: 150 },
    { field: 'complexity_points', headerName: 'Actual Points', width: 150 },
    { field: 'accepted_complexity_points', headerName: 'Accepted Points', width: 150 }
  ];
  
  const engineeringColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'cyclomatic_complexity', headerName: 'Cyclomatic complexity', width: 150 },
    { field: 'maintainability_index', headerName: 'Maintainability Index', width: 120 },
    { field: 'code_coverage', headerName: 'Code coverage', width: 120 },
    { field: 'total_loc', headerName: 'Total LoC', width: 120 },
    { field: 'build_failure_rate', headerName: 'Build failure rate', width: 120 }
  ];
  
  const qualityColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 's1_defect_count', headerName: 'S1 defects', width: 150 },
    { field: 's2_defect_count', headerName: 'S2 defects', width: 120 },
    { field: 's3_defect_count', headerName: 'S3 defects', width: 120 },
    { field: 's4_defect_count', headerName: 'S4 defects', width: 120 },
    { field: 's5_defect_count', headerName: 'S5 defects', width: 120 },
    { field: 'uat_defect_count', headerName: 'UAT defects', width: 120 },
    { field: 'production_defect_count', headerName: 'Production defects', width: 120 },
    { field: 'dismissed_defect_count', headerName: 'Dismissed defects', width: 120 }
  ];
  
  const costColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'baseline_cost', headerName: 'Baseline Cost', width: 150 },
    { field: 'onsite_cost', headerName: 'Onsite Cost', width: 150 },
    { field: 'offshore_cost', headerName: 'Offshore Cost', width: 150 },
    { field: 'other_cost', headerName: 'Other Cost', width: 150 }
  ];

  const serviceRequestColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'P1_ticket_count', headerName: 'P1 Count', width: 100 },
    { field: 'P2_ticket_count', headerName: 'P2 Count', width: 100 },
    { field: 'P3_ticket_count', headerName: 'P3 Count', width: 100 },
    { field: 'P4_ticket_count', headerName: 'P4 Count', width: 100 },
    { field: 'P5_ticket_count', headerName: 'P5 Count', width: 100 },
    { field: 'backlog_actual', headerName: 'Backlog', width: 100 },
    { field: 'MTTR', headerName: 'MTTR', width: 100 },
    { field: 'number_of_tickets_reopened', headerName: 'Reopened', width: 100 }
  ];

  const serviceConfigurationColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'total_configuration_items_count', headerName: 'Total CIs', width: 120, type: 'number' },
    { field: 'no_of_cofiguration_items_added', headerName: 'CIs Added', width: 110, type: 'number' },
    { field: 'no_of_cofiguration_items_removed', headerName: 'CIs Removed', width: 120, type: 'number' },
    { field: 'no_of_configuration_items_updated', headerName: 'CIs Updated', width: 120, type: 'number' },
    { field: 'no_of_incidents', headerName: 'Incidents', width: 100, type: 'number' },
    { field: 'no_of_problems', headerName: 'Problems', width: 100, type: 'number' },
    { field: 'no_of_duplicate_configuration_items', headerName: 'Duplicates', width: 110, type: 'number' }
  ];

  const changeManagementColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'standard_changes_actual', headerName: 'Standard Changes', width: 140 },
    { field: 'normal_changes_actual', headerName: 'Normal Changes', width: 140 },
    { field: 'emergency_changes_actual', headerName: 'Emergency Changes', width: 140 },
    { field: 'standard_changes_success', headerName: 'Standard Success', width: 140 },
    { field: 'normal_changes_success', headerName: 'Normal Success', width: 140 },
    { field: 'emergency_changes_success', headerName: 'Emergency Success', width: 140 },
    { field: 'standard_changes_rollback', headerName: 'Standard Rollback', width: 140 },
    { field: 'normal_changes_rollback', headerName: 'Normal Rollback', width: 140 },
    { field: 'emergency_changes_rollback', headerName: 'Emergency Rollback', width: 140 }
  ];

  const incidentManagementColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'p1_ticket_count_baseline', headerName: 'P1 Ticket Count (Baseline)', width: 200 },
    { field: 'p1_ticket_count', headerName: 'P1 Ticket Count', width: 160 },
    { field: 'p2_ticket_count_baseline', headerName: 'P2 Ticket Count (Baseline)', width: 200 },
    { field: 'p2_ticket_count', headerName: 'P2 Ticket Count', width: 160 },
    { field: 'p3_ticket_count_baseline', headerName: 'P3 Ticket Count (Baseline)', width: 200 },
    { field: 'p3_ticket_count', headerName: 'P3 Ticket Count', width: 160 },
    { field: 'p4_ticket_count_baseline', headerName: 'P4 Ticket Count (Baseline)', width: 200 },
    { field: 'p4_ticket_count', headerName: 'P4 Ticket Count', width: 160 },
    { field: 'p5_ticket_count_baseline', headerName: 'P5 Ticket Count (Baseline)', width: 200 },
    { field: 'p5_ticket_count', headerName: 'P5 Ticket Count', width: 160 },
    { field: 'p1_tickets_responded_within_sla', headerName: 'P1 Tickets Responded Within SLA', width: 250 },
    { field: 'p2_tickets_responded_within_sla', headerName: 'P2 Tickets Responded Within SLA', width: 250 },
    { field: 'p3_tickets_responded_within_sla', headerName: 'P3 Tickets Responded Within SLA', width: 250 },
    { field: 'p4_tickets_responded_within_sla', headerName: 'P4 Tickets Responded Within SLA', width: 250 },
    { field: 'p5_tickets_responded_within_sla', headerName: 'P5 Tickets Responded Within SLA', width: 250 },
    { field: 'p1_average_response_time', headerName: 'P1 Average Response Time', width: 160 },
    { field: 'p2_average_response_time', headerName: 'P2 Average Response Time', width: 160 },
    { field: 'p3_average_response_time', headerName: 'P3 Average Response Time', width: 160 },
    { field: 'p4_average_response_time', headerName: 'P4 Average Response Time', width: 160 },
    { field: 'p5_average_response_time', headerName: 'P5 Average Response Time', width: 160 },
    { field: 'p1_tickets_resolved_within_sla', headerName: 'P1 Tickets Resolved Within SLA', width: 250 },
    { field: 'p2_tickets_resolved_within_sla', headerName: 'P2 Tickets Resolved Within SLA', width: 250 },
    { field: 'p3_tickets_resolved_within_sla', headerName: 'P3 Tickets Resolved Within SLA', width: 250 },
    { field: 'p4_tickets_resolved_within_sla', headerName: 'P4 Tickets Resolved Within SLA', width: 250 },
    { field: 'p5_tickets_resolved_within_sla', headerName: 'P5 Tickets Resolved Within SLA', width: 250 },
    { field: 'p1_average_resolution_time', headerName: 'P1 Average Resolution Time', width: 160 },
    { field: 'p2_average_resolution_time', headerName: 'P2 Average Resolution Time', width: 160 },
    { field: 'p3_average_resolution_time', headerName: 'P3 Average Resolution Time', width: 160 },
    { field: 'p4_average_resolution_time', headerName: 'P4 Average Resolution Time', width: 160 },
    { field: 'p5_average_resolution_time', headerName: 'P5 Average Resolution Time', width: 160 },
    { field: 'backlog_baseline', headerName: 'Backlog (Baseline)', width: 160 },
    { field: 'backlog_actual', headerName: 'Backlog (Actual)', width: 160 },
    { field: 'MTTR', headerName: 'MTTR', width: 100 },
    { field: 'number_of_tickets_reopened', headerName: 'Number of Tickets Reopened', width: 200 }
  ];

  const problemManagementColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'number_of_rcas', headerName: 'Number of RCAs', width: 100 },
    { field: 'problem_tickets_identified', headerName: 'Problem Tickets Identified', width: 200},
    { field: 'number_of_incidents_associated_with_identified_pts', headerName: 'Number of Incidents associated with identified problem tickets', width: 350},
    { field: 'problem_tickets_implemented', headerName: 'Problem Tickets implemented', width: 200},
    { field: 'number_of_incidents_associated_with_implemented_pts', headerName: 'Number of Incidents associated with implemented problem tickets', width: 350},
  ];

  const rdmColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'number_of_releases', headerName: 'Number of releases', width: 200},
    { field: 'automated_release_target', headerName: 'Automated release target', width: 250},
    { field: 'number_of_automated_releases', headerName: 'Number of automated releases', width: 350},
    { field: 'number_of_emergency_releases', headerName: 'Number of emergency releases', width: 350},
    { field: 'number_of_unauthorized_releases', headerName: 'Number of unauthorized releases', width: 350},
    { field: 'number_of_rolledback_releases', headerName: 'Number of rolledback releases', width: 350},
    { field: 'number_of_releases_with_hotfixes', headerName: 'Number of releases with hotfixes', width: 400},
    { field: 'number_of_hotfixes', headerName: 'Number of hotfixes', width: 200},
    { field: 'number_of_incidents', headerName: 'Number of incidents', width: 200},
    { field: 'number_of_releases_without_UAT', headerName: 'Number of releases without UAT', width: 450},
  ];
  
  const accessManagementColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'num_access_requests_processed', headerName: 'Processed', width: 110 },
    { field: 'num_access_requests_approved', headerName: 'Approved', width: 100 },
    { field: 'num_access_requests_rejected', headerName: 'Rejected', width: 100 },
    { field: 'num_access_requests_escalated', headerName: 'Escalated', width: 100 },
    { field: 'num_active_accounts', headerName: 'Active Accts', width: 110 },
    { field: 'num_dormant_accounts', headerName: 'Dormant Accts', width: 110 },
    { field: 'num_privileged_accounts', headerName: 'Privileged', width: 100 },
    { field: 'num_unauthorized_access_incidents', headerName: 'Unauthorized', width: 120 },
    { field: 'num_accesses_provided_within_sla', headerName: 'Provided (SLA)', width: 130 },
    { field: 'num_accesses_revoked_within_sla', headerName: 'Revoked (SLA)', width: 130 },
  ];

  const eventManagementColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'number_of_info', headerName: 'Number of INFO', width: 160 },
    { field: 'number_of_warn', headerName: 'Number of WARN', width: 160 },
    { field: 'number_of_exception', headerName: 'Number of EXCEPTION', width: 180},
    { field: 'number_of_info_automated', headerName: 'Number of INFO - Automated', width: 250},
    { field: 'number_of_warn_automated', headerName: 'Number of WARN - Automated', width: 250},
    { field: 'number_of_exception_automated', headerName: 'Number of EXCEPTION - Automated', width: 300},
    { field: 'number_of_noise', headerName: 'Number of noise', width: 160},
    { field: 'number_of_incident_events', headerName: 'Number of Incident Events', width: 250},
    { field: 'number_of_events_responded_within_SLA', headerName: 'Number of Events Responded Within SLA', width: 450},
    { field: 'number_of_events_resolved_with_SLA', headerName: 'Number of Events Resolved With SLA', width: 450},
    { field: 'number_of_events_escalated', headerName: 'Number of Events Escalated', width: 200}
  ];

  const staffingColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'planned_headcount', headerName: 'Planned Headcount', width: 180 },
    { field: 'actual_headcount', headerName: 'Actual Headcount', width: 180 },
  ];

  const staffQualityColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'attrition_rate', headerName: 'Attrition Rate', width: 160 },
    { field: 'average_experience', headerName: 'Avg Experience', width: 160 },
  ];

  const obligationsColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'owner', headerName: 'Owner', width: 150 },
    { field: 'due_date', headerName: 'Due Date', width: 150 },
    { field: 'fulfillment_date', headerName: 'Fulfillment Date', width: 150 },
    { field: 'at risk', headerName: 'At Risk', width: 120 },
    { field: 'penalty_exposure', headerName: 'Penalty Exposure', width: 150 },
    { field: 'penalty_paid', headerName: 'Penalty Paid', width: 120 },
    { field: 'evidence', headerName: 'Evidence', width: 150 },
  ];

  const financialColumns: GridColDef[] = [
    { field: 'review_entity_name', headerName: 'Review Entity', width: 180 },
    ...METRIC_LEVEL_COLUMNS,
    { field: 'metric_date', headerName: 'Metric Date', width: 140 },
    { field: 'revenue_recognized_plan', headerName: 'Revenue Recognized Plan', width: 200 },
    { field: 'revenue_recognized_ytd', headerName: 'Revenue Recognized YTD', width: 200 },
    { field: 'planned_cost', headerName: 'Planned Cost', width: 160 },
    { field: 'cost_onsite', headerName: 'Cost Onsite', width: 160 },
    { field: 'cost_offshore', headerName: 'Cost Offshore', width: 160 },
    { field: 'cost_others', headerName: 'Cost Others', width: 160 },
    { field: 'invoiced_amount', headerName: 'Invoiced Amount', width: 180 },
    { field: 'dso', headerName: 'DSO', width: 140 }
  ];

  // Get column definition based on metric type key
  const getColumnsForMetricType = (metricKey: string): GridColDef[] => {
    switch (metricKey) {
      case 'schedule': return scheduleColumns;
      case 'effort': return effortColumns;
      case 'scope': return scopeColumns;
      case 'engineering': return engineeringColumns;
      case 'quality': return qualityColumns;
      case 'cost': return costColumns;
      case 'serviceRequest': return serviceRequestColumns;
      case 'changeManagement': return changeManagementColumns;
      case 'serviceConfiguration': return serviceConfigurationColumns;
      case 'incidentManagement': return incidentManagementColumns;
      case 'problemManagement': return problemManagementColumns;
      case 'releaseAndDeploymentManagement': return rdmColumns;
      case 'eventManagement': return eventManagementColumns;
      case 'accessManagement': return accessManagementColumns;
      case 'staffing': return staffingColumns;
      case 'staffQuality': return staffQualityColumns;
      case 'obligations': return obligationsColumns;
      case 'financial': return financialColumns;
      default: return [];
    }
  };

  // Get row ID based on metric type
  const getRowId = (row: any, _metricKey: string) => {
    const level = row.metric_identifier_type || 'UNKNOWN';
    const instance = row.metric_identifier_name || row.milestone_name || row.asset_group || row.obligation_name || 'default';
    return `${row.review_entity_id}_${level}_${instance}_${row.metric_date || 'no_date'}`;
  };

  /* ================= Helpers ================= */
  const entitySelected = selectedEntityId !== '';
  const hasDimensions = sortedDimensions.length > 0;

  return (
    <Container maxWidth="xl">
      <TitleBar title="View Metrics Data" />
      
      {/* Back Button + Metadata Icon */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/view-metrics')}
        >
          Back to View Metrics
        </Button>
        <Tooltip title="View Metric Input Metadata">
          <IconButton color="primary" onClick={handleOpenMetadata}>
            <InfoOutlined />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Metadata Dialog */}
      <Dialog open={metadataDialogOpen} onClose={() => setMetadataDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Metric Input Metadata</DialogTitle>
        <DialogContent dividers>
          {metadataLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : metadataError ? (
            <Alert severity="error">{metadataError}</Alert>
          ) : metadata && Object.keys(metadata).length > 0 ? (
            Object.entries(metadata)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([dimension, entries]) => (
                <Accordion key={dimension} defaultExpanded={false}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                      {dimension.replace(/_/g, ' ')}
                    </Typography>
                    <Chip label={`${entries.length} entities`} size="small" sx={{ ml: 1 }} />
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity Name</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Total Metrics</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Earliest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Latest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Last Loaded</Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {entries.map((entry) => (
                            <TableRow key={entry.review_entity_id}>
                              <TableCell>{entry.review_entity_name || entry.review_entity_id}</TableCell>
                              <TableCell>{entry.total_metrics}</TableCell>
                              <TableCell>{entry.earliest_metric_date || 'N/A'}</TableCell>
                              <TableCell>{entry.latest_metric_date || 'N/A'}</TableCell>
                              <TableCell>{entry.last_loaded_date ? new Date(entry.last_loaded_date).toLocaleString() : 'N/A'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))
          ) : (
            <Typography variant="body2" color="text.secondary">No metadata available.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {optionsLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }}>
          <CircularProgress size={24} />
          <Typography>Loading filters...</Typography>
        </Box>
      ) : optionsError ? (
        <Alert severity="error">{optionsError}</Alert>
      ) : (
        <>
          {/* Entity selection panel */}
          <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FilterList sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Select Review Entity</Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="entity-type-label">Entity Type</InputLabel>
                  <Select
                    labelId="entity-type-label"
                    value={filterEntityType}
                    label="Entity Type"
                    onChange={(e) => setFilterEntityType(e.target.value as string)}
                  >
                    <MenuItem value="">
                      <em>All Types</em>
                    </MenuItem>
                    {entityTypeOptions.map((t) => (
                      <MenuItem key={t} value={t}>{t}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  size="small"
                  options={treeOptions}
                  value={treeOptions.find((o) => o.entity.id === selectedEntityId) || null}
                  onChange={(_, newValue) => setSelectedEntityId(newValue ? newValue.entity.id : '')}
                  getOptionLabel={(option) => option.entity.name}
                  renderOption={(props, option) => (
                    <li {...props} key={option.entity.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', pl: option.depth * 2.5 }}>
                        {option.hasChildren ? (
                          <FolderOpen sx={{ fontSize: 18, mr: 0.75, color: 'warning.main' }} />
                        ) : (
                          <Description sx={{ fontSize: 18, mr: 0.75, color: 'action.active' }} />
                        )}
                        <Box>
                          <Typography variant="body2">{option.entity.name}</Typography>
                          {option.entity.review_entity_type?.display_label && (
                            <Typography variant="caption" color="text.secondary">
                              {option.entity.review_entity_type.display_label}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </li>
                  )}
                  filterOptions={(options, { inputValue }) => {
                    if (!inputValue) return options;
                    const lower = inputValue.toLowerCase();
                    // Find matching entity IDs
                    const matchIds = new Set<number>();
                    options.forEach((o) => {
                      if (o.entity.name.toLowerCase().includes(lower)) {
                        matchIds.add(o.entity.id);
                        // Also include all ancestors so tree context is visible
                        o.entity.parent_ids?.forEach((pid) => matchIds.add(pid));
                      }
                    });
                    return options.filter((o) => matchIds.has(o.entity.id));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Review Entity" />
                  )}
                  isOptionEqualToValue={(option, value) => option.entity.id === value.entity.id}
                  ListboxProps={{ sx: { maxHeight: 400 } }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Prompt when no entity selected */}
          {!entitySelected && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Select a Review Entity above to load metrics data across all dimensions.
              </Typography>
            </Paper>
          )}

          {/* Dimension tabs + content */}
          {entitySelected && hasDimensions && (
            <>
              <Paper sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Tabs
                  sx={{ flex: 1 }}
                  value={activeTabIndex}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  {sortedDimensions.map((dim) => {
                    const st = dimensionData[dim]?.status || 'idle';
                    return (
                      <Tab
                        key={dim}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {dim}
                            <DimStatusIcon status={st} />
                          </Box>
                        }
                      />
                    );
                  })}
                </Tabs>
                <Tooltip title="View Metadata for Selected Entity">
                  <IconButton color="primary" onClick={handleOpenFilteredMetadata} sx={{ mr: 1 }}>
                    <InfoOutlined />
                  </IconButton>
                </Tooltip>
                </Box>
              </Paper>

              {/* Filtered Metadata Dialog */}
              <Dialog open={filteredMetaDialogOpen} onClose={() => setFilteredMetaDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Metric Input Metadata — {selectedEntity?.name}</DialogTitle>
                <DialogContent dividers>
                  {filteredMetaLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : filteredMetaError ? (
                    <Alert severity="error">{filteredMetaError}</Alert>
                  ) : filteredMetadata && Object.keys(filteredMetadata).length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Dimension</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Total Metrics</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Earliest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Latest Date</Typography></TableCell>
                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Last Loaded</Typography></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {Object.entries(filteredMetadata)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([dim, entries]) =>
                              entries.map((entry) => (
                                <TableRow key={dim}>
                                  <TableCell sx={{ textTransform: 'capitalize' }}>{dim.replace(/_/g, ' ')}</TableCell>
                                  <TableCell>{entry.total_metrics}</TableCell>
                                  <TableCell>{entry.earliest_metric_date || 'N/A'}</TableCell>
                                  <TableCell>{entry.latest_metric_date || 'N/A'}</TableCell>
                                  <TableCell>{entry.last_loaded_date ? new Date(entry.last_loaded_date).toLocaleString() : 'N/A'}</TableCell>
                                </TableRow>
                              ))
                            )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No metadata available for this entity.</Typography>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setFilteredMetaDialogOpen(false)}>Close</Button>
                </DialogActions>
              </Dialog>

              {/* Tab content */}
              {currentDimState?.status === 'loading' && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {currentDimState?.status === 'error' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to load metrics data for dimension "{activeDimName}".
                </Alert>
              )}

              {currentDimState?.status === 'empty' && (
                <Alert severity="info">
                  No metrics data found for dimension "{activeDimName}".
                </Alert>
              )}

              {currentDimState?.status === 'loaded' && currentColumnKey && (
                <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {activeDimName} Metrics ({metricsData.length} records)
                  </Typography>
                  <Box sx={{ height: 500, width: '100%' }}>
                    <DataGrid
                      rows={metricsData}
                      columns={getColumnsForMetricType(currentColumnKey)}
                      getRowId={(row) => getRowId(row, currentColumnKey)}
                      initialState={{
                        pagination: {
                          paginationModel: { pageSize: 10, page: 0 },
                        },
                      }}
                      pageSizeOptions={[10, 20, 50, 100]}
                      disableRowSelectionOnClick
                    />
                  </Box>
                </Paper>
              )}
            </>
          )}

          {/* No dimensions available for selected entity */}
          {entitySelected && !hasDimensions && (
            <Alert severity="warning">
              No review dimensions are associated with this review entity type. Please check the data setup.
            </Alert>
          )}
        </>
      )}
    </Container>
  );
};

export default ViewMetricsData;
